/**
 * 手动老客户入 CRM 后的跟进工作区：记录沟通和下次行动，历史记录按当前客户归属读取。
 * 请求中断时只持久保存随机操作键、归属版本和加盐摘要，不把客户反馈写入浏览器存储；
 * 跨标签页用浏览器锁协调，重新打开时由服务端确认上次结果，避免重复或长期锁死。
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "../ui/sonner";
import { fmtD, fmtDT } from "../../lib/date";

const EMPTY = { method: "Phone", response: "", nextAction: "", nextFollowUpDate: "", activityDate: "" };
const METHODS = [["Phone", "电话", "Phone"], ["SMS", "短信", "SMS"], ["Email", "邮件", "Email"],
  ["WeChat", "微信", "WeChat"], ["Zoom", "视频", "Video"], ["Site Visit", "现场拜访", "Site visit"], ["Office Meeting", "办公室见面", "Office meeting"]];

function readOperationState(storageKey) {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return { revision: "", pending: null };
    const state = JSON.parse(raw);
    const saved = state.pending;
    if (/^[a-f0-9]{32}$/.test(state.revision || "") && (saved === null ||
        (/^[a-f0-9]{32}$/.test(saved?.key || "") && /^[a-f0-9]{64}$/.test(saved?.digest || "")
        && typeof saved?.ownershipVersion === "string" && saved.ownershipVersion.length > 0 && saved.ownershipVersion.length <= 100))) return state;
  } catch { /* 受限浏览器或被破坏的记录不能安全地重新生成操作键。 */ }
  return { invalid: true, pending: { invalid: true } };
}

function newKey() {
  if (!globalThis.crypto?.getRandomValues || !globalThis.crypto?.subtle || typeof TextEncoder === "undefined") throw new Error("secure-storage-unavailable");
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (n) => n.toString(16).padStart(2, "0")).join("");
}

function writeOperationState(storageKey, pending) {
  // 完成后保留不含客户资料的版本戳；其他标签即使尚未收到 storage 事件也能发现自己已过期。
  const state = { revision: newKey(), pending };
  window.localStorage.setItem(storageKey, JSON.stringify(state));
  return state;
}

async function fingerprint(key, body) {
  return sha256(`${key}:${JSON.stringify(body)}`);
}

async function sha256(text) {
  const input = new TextEncoder().encode(text);
  const hash = await globalThis.crypto.subtle.digest("SHA-256", input);
  return Array.from(new Uint8Array(hash), (n) => n.toString(16).padStart(2, "0")).join("");
}

/** 稳定账号及客户共同隔离重试记录；是否允许写由服务端动作权限快照传入。 */
export function PrivateCustomerFollowups({ api, leadId, actorKey, ownershipVersion, zh, canWrite, onChanged }) {
  const [storageKey, setStorageKey] = useState("");
  const [storageReady, setStorageReady] = useState(false);
  const [revision, setRevision] = useState("");
  const [pending, setPending] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [needsReview, setNeedsReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const isVisit = ["Site Visit", "Office Meeting"].includes(form.method);
  const locked = busy || (!!pending && !restoring);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/crm/leads/${leadId}/followups`, { timeout: 15000 });
      setItems(Array.isArray(data) ? data : []); setLoadError("");
    } catch {
      setItems([]); setLoadError(zh ? "跟进记录加载失败，请刷新或检查客户权限。" : "Unable to load follow-ups. Refresh or check access.");
    }
    setLoading(false);
  }, [api, leadId, zh]);
  useEffect(() => { load(); }, [load]);

  const refreshParent = useCallback(() => {
    try { Promise.resolve(onChangedRef.current?.()).catch(() => {}); } catch { /* 父页面刷新失败不影响已保存的记录。 */ }
  }, []);

  const operationStatus = useCallback(async (saved) => {
    const { data } = await api.get(`/crm/leads/${leadId}/followup-operations/${saved.key}`, { params: { ownershipVersion: saved.ownershipVersion }, timeout: 15000 });
    if (data?.status === "stale") {
      // 旧客户端可能不携带归属版本，必须原子放弃后才能确定晚到请求不会写入。
      const result = await api.post(`/crm/leads/${leadId}/followup-operations/${saved.key}/cancel`, {}, { timeout: 30000 });
      if (!["confirmed", "unconfirmed"].includes(result.data?.status)) throw new Error("invalid-operation-status");
      return result.data.status;
    }
    if (!["confirmed", "unconfirmed", "pending"].includes(data?.status)) throw new Error("invalid-operation-status");
    return data.status;
  }, [api, leadId]);

  // 检查前必须持有跨标签页锁：另一个标签可能还在提交，不能把它的进行中请求当成失败。
  const checkPending = useCallback(async (key, saved) => navigator.locks.request(`${key}.lock`, { mode: "exclusive", ifAvailable: true }, async (lock) => {
    if (!lock) return { status: "busy" };
    const state = readOperationState(key);
    const current = state.pending;
    if (!current) return { status: "cleared", revision: state.revision };
    if (state.invalid || current.key !== saved.key) return { status: "changed", state };
    const status = await operationStatus(current);
    if (["confirmed", "unconfirmed"].includes(status)) return { status, revision: writeOperationState(key, null).revision };
    return { status: "pending" };
  }), [operationStatus]);

  useEffect(() => {
    if (!canWrite) return undefined;
    let active = true;
    const prepare = async () => {
      try {
        if (!actorKey || !globalThis.crypto?.subtle || !globalThis.crypto?.getRandomValues || !navigator.locks?.request || typeof TextEncoder === "undefined") throw new Error("unsupported");
        // 存储键也使用摘要，不把账号或客户 ID 直接写入本机浏览器。
        const key = `crm.private-followup.${await sha256(JSON.stringify([actorKey, leadId]))}`;
        const state = readOperationState(key);
        const saved = state.pending;
        if (!active) return;
        setStorageKey(key); setRevision(state.revision || ""); setPending(saved); setRestoring(!!saved);
        if (saved && !saved.invalid) {
          const result = await checkPending(key, saved);
          const status = result.status;
          if (!active) return;
          if (status === "confirmed" || status === "unconfirmed" || status === "cleared") {
            setRevision(result.revision || ""); setPending(null); setRestoring(false);
            setNotice(status === "confirmed" ? (zh ? "上次跟进已保存，请查看下方记录。" : "The previous follow-up was saved. Check the history below.")
              : (zh ? "上次待确认操作已结束，请核对记录后继续。" : "The previous operation has ended. Check the history before continuing."));
            await load();
            refreshParent();
          } else if (status === "busy") setNotice(zh ? "另一窗口正在处理上次提交，请稍后重试。" : "Another window is processing the previous submission. Retry shortly.");
        }
      } catch {
        if (active) setError(zh ? "无法核对待确认提交，请检查网络和浏览器设置。" : "Unable to check a pending submission. Check your connection and browser settings.");
      } finally { if (active) setStorageReady(true); }
    };
    prepare();
    return () => { active = false; };
  }, [actorKey, leadId, canWrite, checkPending, load, refreshParent, zh]);

  useEffect(() => {
    if (!storageKey) return undefined;
    const onStorage = (event) => {
      if (event.key !== storageKey) return;
      const state = readOperationState(storageKey);
      setRevision(state.revision || ""); setPending(state.pending); setRestoring(!!state.pending);
      if (!state.pending) {
        // 另一个窗口完成操作后保留此处独立草稿，先让用户核对历史，避免误清未提交内容或重复提交。
        setNeedsReview(true);
        setNotice(zh ? "另一窗口已处理上次提交，请核对跟进记录；当前输入已保留。" : "Another window handled a submission. Check the history; your draft is preserved.");
        load();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey, load, zh]);

  const submit = async (event) => {
    event.preventDefault();
    // React 的 busy 更新不一定立刻重渲染；同步锁住同一渲染帧的重复提交。
    if (submitting.current || needsReview) return;
    if (!form.response.trim()) { setError(zh ? "请填写沟通结果。" : "Enter the conversation result."); return; }
    if (isVisit && !form.activityDate) { setError(zh ? "请填写实际见面日期。" : "Enter the actual meeting date."); return; }
    if (pending?.invalid) { setError(zh ? "无法核对上次提交，请联系管理员处理。" : "Unable to verify the previous submission. Contact an administrator."); return; }
    if (!storageKey || !storageReady) { setError(zh ? "浏览器暂时无法安全提交。" : "The browser cannot safely submit right now."); return; }
    let submitted = false;
    let wasPending = false;
    let refreshAfter = false;
    let remindAfter = false;
    submitting.current = true;
    setBusy(true); setError(""); setNotice("");
    try {
      await navigator.locks.request(`${storageKey}.lock`, { mode: "exclusive", ifAvailable: true }, async (lock) => {
        if (!lock) { setError(zh ? "另一窗口正在提交，请稍后重试。" : "Another window is submitting. Retry shortly."); return; }
        try {
        const state = readOperationState(storageKey);
        const saved = state.pending;
        if (state.invalid) { setPending(saved); setError(zh ? "无法核对上次提交，请联系管理员处理。" : "Unable to verify the previous submission. Contact an administrator."); return; }
        if (state.revision !== revision) {
          setRevision(state.revision); setPending(saved); setRestoring(!!saved);
          if (!saved) setNeedsReview(true);
          setNotice(zh ? "另一窗口已处理或更新上次提交，请核对跟进记录。" : "Another window handled the previous operation. Check the history.");
          refreshAfter = true; return;
        }
        if (pending && (!saved || saved.key !== pending.key)) {
          // 其他标签已处理或替换旧操作，不能把当前旧草稿自动当成新跟进再提交。
          setPending(saved); setRestoring(!!saved); if (!saved) setNeedsReview(true);
          setNotice(zh ? "另一窗口已处理或更新上次提交，请核对跟进记录。" : "Another window handled the previous operation. Check the history.");
          refreshAfter = true; return;
        }
        if (saved && !pending) {
          setPending(saved); setRestoring(true);
          setError(zh ? "另一窗口存在待确认提交，请先核对原内容。" : "Another window has a pending submission. Check the original content."); return;
        }
        wasPending = !!saved;
        if (saved) {
          const status = await operationStatus(saved);
          if (["confirmed", "unconfirmed"].includes(status)) {
            setRevision(writeOperationState(storageKey, null).revision); setPending(null); setRestoring(false);
            if (status === "confirmed") setNeedsReview(true);
            setNotice(status === "confirmed" ? (zh ? "上次跟进已保存，请查看下方记录；当前输入已保留。" : "The previous follow-up was saved. Check the history; your draft is preserved.")
              : (zh ? "上次跟进未保存，请核对后重新提交。" : "The previous follow-up was not saved. Check it before submitting again."));
            refreshAfter = true; return;
          }
        }
        const expectedVersion = saved?.ownershipVersion || ownershipVersion || "initial";
        const body = { method: form.method, response: form.response.trim(), nextAction: form.nextAction.trim(),
          nextFollowUpDate: form.nextFollowUpDate || null, ...(isVisit ? { activityDate: form.activityDate } : {}), expectedOwnershipVersion: expectedVersion };
        const key = saved?.key || newKey();
        const digest = await fingerprint(key, body);
        if (saved && digest !== saved.digest) {
          setError(zh ? "与上次提交的内容不一致，请填回原内容后重试。" : "The content differs from the previous submission. Restore it before retrying."); return;
        }
        if (!saved) {
          const operation = { key, digest, ownershipVersion: expectedVersion };
          // 在发送前持久化不含客户资料的凭据；浏览器重启后仍可检查并重用同一操作键。
          setRevision(writeOperationState(storageKey, operation).revision);
          setPending(operation); setRestoring(false);
        }
        submitted = true;
        await api.post(`/crm/leads/${leadId}/followups`, { ...body, idempotencyKey: key }, { timeout: 30000 });
        setRevision(writeOperationState(storageKey, null).revision);
        setPending(null); setRestoring(false); setForm(EMPTY);
        toast.success(zh ? "已记录跟进" : "Follow-up recorded");
        refreshAfter = true; remindAfter = true;
        } catch (err) {
          const detail = err?.response?.data?.detail;
          if (detail?.code === "private_followup_unconfirmed_conflict") {
            try {
              // 字段发生过变化并不证明旧请求已经停止：其他编辑可能又把字段改回去。
              // 必须在锁内原子核对 / 放弃，先完成的一方决定结果，才能安全清理操作键。
              const state = readOperationState(storageKey);
              if (state.invalid || !state.pending?.key) throw new Error("invalid-operation-state");
              const result = await api.post(`/crm/leads/${leadId}/followup-operations/${state.pending.key}/cancel`, {}, { timeout: 30000 });
              if (!["confirmed", "unconfirmed"].includes(result.data?.status)) throw new Error("invalid-operation-status");
              setRevision(writeOperationState(storageKey, null).revision); setPending(null); setRestoring(false);
              setNeedsReview(result.data.status === "confirmed");
              setNotice(result.data.status === "confirmed" ? (zh ? "上次跟进已保存，请核对下方记录；当前输入已保留。" : "The previous follow-up was saved. Check the history; your draft is preserved.")
                : (zh ? "未保存的提交已放弃，请核对后重新提交。" : "The unsaved submission was abandoned. Check it before submitting again."));
              refreshAfter = true;
            } catch {
              setError(zh ? "提交结果尚未确认，请保留原内容并重试。" : "The result is unconfirmed. Retry with the same content.");
            }
          } else if (!wasPending && err?.response?.status === 400) {
            // 初次请求在输入校验阶段失败，不存在已发出的同键候选；持锁清理操作键。
            try { setRevision(writeOperationState(storageKey, null).revision); setPending(null); setRestoring(false); } catch { /* 保留旧状态，避免误以为凭据已清理。 */ }
            setError((typeof detail === "string" ? detail : detail?.message) || (zh ? "提交未保存，请核对后重试。" : "Not saved. Check the details and retry."));
          } else if (!submitted) {
            setError(zh ? "无法核对或保存提交凭据，请检查网络和浏览器设置。" : "Unable to verify or store the submission. Check your connection and browser settings.");
          } else {
            setError(zh ? "提交结果尚未确认，请保留原内容并重试；关闭后需重新填回相同内容。" : "The result is unconfirmed. Retry with the same content; re-enter it if you close this page.");
          }
        }
      });
      // 网络刷新放在锁外；即使详情请求挂起，也不会卡住其他标签对同一客户的操作。
      if (refreshAfter) { await load(); refreshParent(); }
      if (remindAfter) window.dispatchEvent(new Event("reminders-changed"));
    } catch {
      setError(zh ? "浏览器无法协调其他窗口，请稍后重试。" : "The browser could not coordinate other windows. Retry shortly.");
    } finally { submitting.current = false; setBusy(false); }
  };

  const abandon = async () => {
    if (submitting.current || !pending || pending.invalid || !storageKey) return;
    submitting.current = true; setBusy(true); setError(""); setNotice("");
    let refreshAfter = false;
    try {
      await navigator.locks.request(`${storageKey}.lock`, { mode: "exclusive", ifAvailable: true }, async (lock) => {
        if (!lock) { setError(zh ? "另一窗口正在处理提交，请稍后重试。" : "Another window is processing the submission. Retry shortly."); return; }
        const state = readOperationState(storageKey);
        if (state.invalid) { setError(zh ? "无法核对上次提交，请联系管理员处理。" : "Unable to verify the previous submission. Contact an administrator."); return; }
        if (state.revision !== revision || state.pending?.key !== pending.key) {
          setRevision(state.revision); setPending(state.pending); setRestoring(!!state.pending); setNeedsReview(!state.pending);
          setNotice(zh ? "另一窗口已处理或更新上次提交，请核对跟进记录。" : "Another window handled the previous operation. Check the history.");
          refreshAfter = true; return;
        }
        // 服务端在客户文档上原子标记放弃；已保存时返回 confirmed，绝不删除已有记录。
        const { data } = await api.post(`/crm/leads/${leadId}/followup-operations/${pending.key}/cancel`, {}, { timeout: 30000 });
        if (!["confirmed", "unconfirmed"].includes(data?.status)) throw new Error("invalid-operation-status");
        setRevision(writeOperationState(storageKey, null).revision); setPending(null); setRestoring(false);
        setNeedsReview(data.status === "confirmed");
        setNotice(data.status === "confirmed" ? (zh ? "上次跟进已保存，请核对下方记录；当前输入已保留。" : "The previous follow-up was saved. Check the history; your draft is preserved.")
          : (zh ? "未保存的提交已放弃，当前输入已保留。" : "The unsaved submission was abandoned; your draft is preserved."));
        refreshAfter = true;
      });
      if (refreshAfter) { await load(); refreshParent(); }
    } catch {
      setError(zh ? "暂时无法核对上次提交，请检查网络后重试。" : "Unable to check the previous submission. Check your connection and retry.");
    } finally { submitting.current = false; setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4" data-testid="private-customer-followups">
      {canWrite && <form onSubmit={submit} className="space-y-3">
        {pending && <p role="status" className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{pending.invalid ? (zh ? "无法核对上次提交，请联系管理员处理。" : "Unable to verify the previous submission. Contact an administrator.") : (zh ? "上次提交尚未确认，请使用原内容重试。" : "The previous submission is unconfirmed. Retry with the original content.")}</p>}
        {notice && <p role="status" className="text-sm text-slate-600">{notice}</p>}
        {needsReview && <Button type="button" variant="outline" data-testid="private-followup-reviewed" onClick={() => setNeedsReview(false)}>{zh ? "已核对，继续填写" : "Checked; continue"}</Button>}
        <div className="grid gap-3 md:grid-cols-2">
          <label className="space-y-1 text-sm text-slate-600"><span>{zh ? "沟通方式" : "Method"}</span>
            <Select value={form.method} disabled={locked} onValueChange={(method) => setForm((current) => ({ ...current, method }))}>
              <SelectTrigger aria-label={zh ? "沟通方式" : "Method"}><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map(([value, cn, en]) => <SelectItem key={value} value={value}>{zh ? cn : en}</SelectItem>)}</SelectContent>
            </Select>
          </label>
          <label className="space-y-1 text-sm text-slate-600"><span>{zh ? "下次跟进日期（选填）" : "Next follow-up date (optional)"}</span>
            <Input type="date" value={form.nextFollowUpDate} disabled={locked} onChange={(e) => setForm((current) => ({ ...current, nextFollowUpDate: e.target.value }))} />
          </label>
          {isVisit && <label className="space-y-1 text-sm text-slate-600"><span>{zh ? "实际见面日期" : "Actual meeting date"}</span>
            <Input type="date" value={form.activityDate} disabled={locked} onChange={(e) => setForm((current) => ({ ...current, activityDate: e.target.value }))} />
          </label>}
        </div>
        <label className="block space-y-1 text-sm text-slate-600"><span>{zh ? "沟通结果" : "Conversation result"}</span>
          <Textarea data-testid="private-followup-response" value={form.response} disabled={locked} onChange={(e) => setForm((current) => ({ ...current, response: e.target.value }))} rows={3} />
        </label>
        <label className="block space-y-1 text-sm text-slate-600"><span>{zh ? "下一步行动（选填）" : "Next action (optional)"}</span>
          <Input value={form.nextAction} disabled={locked} onChange={(e) => setForm((current) => ({ ...current, nextAction: e.target.value }))} />
        </label>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <Button type="submit" disabled={busy || needsReview || !storageReady || !storageKey || !!pending?.invalid} data-testid="private-followup-submit">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{pending ? (zh ? "重试提交" : "Retry") : (zh ? "记录跟进" : "Record follow-up")}
        </Button>
        {pending && !pending.invalid && <Button type="button" variant="outline" className="ml-2" disabled={busy} onClick={abandon} data-testid="private-followup-abandon">{zh ? "核对并放弃未确认提交" : "Check and abandon unconfirmed submission"}</Button>}
      </form>}
      <div className="space-y-3" aria-busy={loading}>
        <h4 className="text-sm font-semibold text-slate-700">{zh ? "跟进记录" : "Follow-up history"}</h4>
        {loading ? <p className="text-sm text-slate-500">{zh ? "加载中…" : "Loading…"}</p>
          : loadError ? <p role="alert" className="text-sm text-red-700">{loadError}</p>
          : items.length === 0 ? <p className="text-sm text-slate-500">{zh ? "暂无跟进记录" : "No follow-ups yet"}</p>
          : items.map((item) => <div key={item.id} className="border-l-2 border-indigo-200 pl-3 text-sm" data-testid={`private-followup-item-${item.id}`}>
            <p className="text-xs text-slate-500">{METHODS.find(([value]) => value === item.method)?.[zh ? 1 : 2] || item.method} · {fmtDT(item.createdAt)} · {item.authorName || item.authorUsername || "—"}</p>
            <p className="mt-1 whitespace-pre-wrap text-slate-700">{item.response || "—"}</p>
            {(item.nextAction || item.nextFollowUpDate) && <p className="mt-1 text-xs text-slate-500">{zh ? "下一步：" : "Next: "}{item.nextAction || "—"}{item.nextFollowUpDate ? ` · ${fmtD(item.nextFollowUpDate)}` : ""}</p>}
          </div>)}
      </div>
    </div>
  );
}
